import React, { useState } from 'react'
import styled from 'styled-components'
import theme from '../../styles/theme'
import { useAuth } from './AuthProvider'

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: white;
  padding: 2rem;
`

const LoginCard = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  padding: 3rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-width: 400px;
  width: 100%;
`

const Logo = styled.img`
  max-height: 120px;
  max-width: 300px;
  height: auto;
  width: auto;
  object-fit: contain;
  margin-bottom: 2rem;
`

const Title = styled.h1`
  color: ${theme.color.text.base};
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
`

const Subtitle = styled.p`
  color: ${theme.color.text.grey};
  font-size: 1rem;
  margin-bottom: 2rem;
  line-height: 1.5;
`

const LoginButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #b8002e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`

const HelpLink = styled.button`
  background: none;
  border: none;
  color: ${theme.color.text.grey};
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1.5rem;
  text-decoration: underline;

  &:hover {
    color: ${theme.color.accent.primary};
  }
`

const ErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.1);
  color: #ff6b6b;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 68, 68, 0.3);
`

const AccessDeniedMessage = styled.div`
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 193, 7, 0.3);
  text-align: left;

  h4 {
    margin: 0 0 0.5rem 0;
    color: #ffc107;
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.5rem;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  padding: 2rem;
  max-width: 450px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`

const ModalTitle = styled.h2`
  color: ${theme.color.text.base};
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
`

const ModalSubtitle = styled.p`
  color: ${theme.color.text.grey};
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  text-align: center;
`

const FormGroup = styled.div`
  margin-bottom: 1rem;
`

const Label = styled.label`
  display: block;
  color: ${theme.color.text.base};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 1rem;
  background: ${theme.color.background.dark};
  color: ${theme.color.text.base};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 1rem;
  background: ${theme.color.background.dark};
  color: ${theme.color.text.base};
  min-height: 120px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`

const SubmitButton = styled.button`
  flex: 1;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #b8002e;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`

const CancelButton = styled.button`
  flex: 1;
  background: transparent;
  color: ${theme.color.text.base};
  border: 1px solid #444;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  color: #4caf50;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid rgba(76, 175, 80, 0.3);
  text-align: center;
`

const ModalErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.1);
  color: #ff6b6b;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 68, 68, 0.3);
  text-align: center;
`

interface LoginPageProps {
  error?: string
  accessDenied?: boolean
}

const LoginPage: React.FC<LoginPageProps> = ({ error, accessDenied }) => {
  const { login, isLoading } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ name: '', email: '', issue: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const handleLogin = async () => {
    try {
      console.log('LoginPage: handleLogin called')
      await login()
      console.log('LoginPage: login completed')
    } catch (err) {
      console.error('LoginPage: Login failed:', err)
    }
  }

  const handleOpenModal = () => {
    setShowModal(true)
    setSubmitStatus(null)
    setFormData({ name: '', email: '', issue: '' })
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSubmitStatus(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.email || !formData.issue) {
      setSubmitStatus({ type: 'error', message: 'All fields are required' })
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('/api/public/access-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Your request has been submitted. An administrator will contact you shortly.' })
        setFormData({ name: '', email: '', issue: '' })
      } else {
        setSubmitStatus({ type: 'error', message: data.error || 'Failed to submit request' })
      }
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Failed to submit request. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <LoginContainer>
      <LoginCard>
        <Logo src="/portal-logo.png" alt="Barry's Admin Portal" />

        <Title>Admin Portal</Title>
        <Subtitle>
          Secure access for Barry's staff and administrators
        </Subtitle>

        {error && (
          <ErrorMessage>
            Login failed: {error}
          </ErrorMessage>
        )}

        {accessDenied && (
          <AccessDeniedMessage>
            <h4>Access Denied</h4>
            <p>You don't have permission to access this application. Please contact your administrator to request access.</p>
            <p>Required groups:</p>
            <ul>
              <li>Barry's-Admin-Portal-Users</li>
              <li>Everyone</li>
              <li>Barry's-Customer-Support</li>
            </ul>
          </AccessDeniedMessage>
        )}

        <LoginButton
          onClick={handleLogin}
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign in with Okta'}
        </LoginButton>

        <HelpLink onClick={handleOpenModal}>
          Request Login Assistance
        </HelpLink>
      </LoginCard>

      {showModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Request Login Assistance</ModalTitle>
            <ModalSubtitle>
              Having trouble logging in? Submit your details and an administrator will assist you.
            </ModalSubtitle>

            {submitStatus?.type === 'success' ? (
              <>
                <SuccessMessage>{submitStatus.message}</SuccessMessage>
                <CancelButton onClick={handleCloseModal} style={{ width: '100%' }}>
                  Close
                </CancelButton>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                {submitStatus?.type === 'error' && (
                  <ModalErrorMessage>{submitStatus.message}</ModalErrorMessage>
                )}

                <FormGroup>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@barrys.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="issue">Issue Description *</Label>
                  <TextArea
                    id="issue"
                    placeholder="Please describe your login issue..."
                    value={formData.issue}
                    onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                    required
                  />
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={handleCloseModal}>
                    Cancel
                  </CancelButton>
                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </SubmitButton>
                </ButtonGroup>
              </form>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </LoginContainer>
  )
}

export default LoginPage
